import type {
  ConfirmedVulnerabilityFindingStateType,
  SignalStatusType,
} from '@terra/shared-schemas-orchestrator';
import {
  BLOCKED_REASON_VALUES,
  blockedReasonFromStatusFilterValue,
  getBlockedStatusFilterValue,
  SignalStatus,
  type BlockedReasonType,
  type BlockedStatusFilterValue,
} from '@terra/shared-schemas-orchestrator';

export const SIGNAL_STATUS_FILTER_VALUES: readonly SignalStatusType[] = [
  SignalStatus.DISPROVEN_VULNERABILITY,
  SignalStatus.POTENTIAL_VULNERABILITY,
  SignalStatus.CONFIRMED_VULNERABILITY,
  SignalStatus.BLOCKED,
  SignalStatus.DUPLICATE,
  SignalStatus.PENDING_ENRICHMENT,
  SignalStatus.FAILED_ENRICHMENT,
] as const;

export const CONFIRMED_VULNERABILITY_FINDING_STATE_STATUS_FILTER = {
  OPEN: 'CONFIRMED_VULNERABILITY_OPEN',
  CLOSED: 'CONFIRMED_VULNERABILITY_CLOSED',
} as const;

type ConfirmedVulnerabilityFindingStateStatusFilterValue =
  (typeof CONFIRMED_VULNERABILITY_FINDING_STATE_STATUS_FILTER)[keyof typeof CONFIRMED_VULNERABILITY_FINDING_STATE_STATUS_FILTER];

export type SignalStatusFilterValue =
  | SignalStatusType
  | ConfirmedVulnerabilityFindingStateStatusFilterValue
  | BlockedStatusFilterValue;

const CONFIRMED_VULNERABILITY_FINDING_STATE_BY_STATUS_FILTER_VALUE: Record<
  ConfirmedVulnerabilityFindingStateStatusFilterValue,
  ConfirmedVulnerabilityFindingStateType
> = {
  [CONFIRMED_VULNERABILITY_FINDING_STATE_STATUS_FILTER.OPEN]: 'open',
  [CONFIRMED_VULNERABILITY_FINDING_STATE_STATUS_FILTER.CLOSED]: 'closed',
};

const STATUS_FILTER_VALUE_BY_CONFIRMED_VULNERABILITY_FINDING_STATE: Record<
  ConfirmedVulnerabilityFindingStateType,
  ConfirmedVulnerabilityFindingStateStatusFilterValue
> = {
  open: CONFIRMED_VULNERABILITY_FINDING_STATE_STATUS_FILTER.OPEN,
  closed: CONFIRMED_VULNERABILITY_FINDING_STATE_STATUS_FILTER.CLOSED,
};

export const ALL_SIGNAL_STATUS_FILTER_VALUES: ReadonlySet<string> = new Set([
  ...SIGNAL_STATUS_FILTER_VALUES,
  ...Object.values(CONFIRMED_VULNERABILITY_FINDING_STATE_STATUS_FILTER),
  ...BLOCKED_REASON_VALUES.map((reason) => getBlockedStatusFilterValue(reason)),
]);

function getTranslatedOptions<T extends string>({
  values,
  namespace,
  t,
  tFilterTooltip,
}: {
  values: readonly T[];
  namespace: string;
  t: (key: string, params?: Record<string, unknown>) => string;
  tFilterTooltip?: (statusKey: string) => string;
}): Array<{ label: string; value: T; tooltip?: string }> {
  return values.map((value) => ({
    label: t(`${namespace}.${value}` as never),
    value,
    tooltip: tFilterTooltip?.(value),
  }));
}

export type SignalStatusFilterOption = {
  label: string;
  value: SignalStatusFilterValue;
  tooltip?: string;
  children?: Array<{ label: string; value: string; tooltip?: string }>;
};

export function getSignalStatusOptions({
  t,
  tFilterTooltip,
  blockedReasonNamespace = 'status',
}: {
  t: (key: string, params?: Record<string, unknown>) => string;
  tFilterTooltip: (statusKey: string) => string;
  blockedReasonNamespace?: string;
}): SignalStatusFilterOption[] {
  return [
    ...getTranslatedOptions({
      values: [
        SignalStatus.DISPROVEN_VULNERABILITY,
        SignalStatus.POTENTIAL_VULNERABILITY,
        SignalStatus.CONFIRMED_VULNERABILITY,
      ] as const,
      namespace: 'status',
      t,
      tFilterTooltip,
    }),
    {
      label: t(
        `status.${CONFIRMED_VULNERABILITY_FINDING_STATE_STATUS_FILTER.OPEN}` as never,
      ),
      value: CONFIRMED_VULNERABILITY_FINDING_STATE_STATUS_FILTER.OPEN,
      tooltip: tFilterTooltip(
        CONFIRMED_VULNERABILITY_FINDING_STATE_STATUS_FILTER.OPEN,
      ),
    },
    {
      label: t(
        `status.${CONFIRMED_VULNERABILITY_FINDING_STATE_STATUS_FILTER.CLOSED}` as never,
      ),
      value: CONFIRMED_VULNERABILITY_FINDING_STATE_STATUS_FILTER.CLOSED,
      tooltip: tFilterTooltip(
        CONFIRMED_VULNERABILITY_FINDING_STATE_STATUS_FILTER.CLOSED,
      ),
    },
    ...getTranslatedOptions({
      values: [SignalStatus.BLOCKED] as const,
      namespace: 'status',
      t,
      tFilterTooltip,
    }).map((option) => ({
      ...option,
      children: BLOCKED_REASON_VALUES.map((reason) => ({
        label: t(`${blockedReasonNamespace}.blockedReason.${reason}` as never),
        value: getBlockedStatusFilterValue(reason),
      })),
    })),
    ...getTranslatedOptions({
      values: [
        SignalStatus.DUPLICATE,
        SignalStatus.PENDING_ENRICHMENT,
        SignalStatus.FAILED_ENRICHMENT,
      ] as const,
      namespace: 'status',
      t,
      tFilterTooltip,
    }),
  ];
}

export function getBlockedStatusFilterValues(
  subtypes: readonly BlockedReasonType[] | undefined,
): BlockedStatusFilterValue[] {
  return subtypes?.map((subtype) => getBlockedStatusFilterValue(subtype)) ?? [];
}

export function countBlockedStatusFilterValues(
  values: readonly string[] | undefined,
): number {
  return (
    values?.filter(
      (value) => blockedReasonFromStatusFilterValue(value) !== null,
    ).length ?? 0
  );
}

export function getSignalStatusSelectionCount(
  selectedValues: readonly string[],
): number {
  const blockedReasonCount = countBlockedStatusFilterValues(selectedValues);
  const nonBlockedReasonValues = selectedValues.filter(
    (value) => !blockedReasonFromStatusFilterValue(value),
  );
  const hasBlockedSelection =
    nonBlockedReasonValues.includes(SignalStatus.BLOCKED) ||
    blockedReasonCount > 0;
  const otherStatuses = nonBlockedReasonValues.filter(
    (value) => value !== SignalStatus.BLOCKED,
  );

  return otherStatuses.length + (hasBlockedSelection ? 1 : 0);
}

export function resolveSignalsStatusFilterBadgeText({
  selectedValues,
  t,
  tSelected,
}: {
  selectedValues: readonly string[];
  t: (key: string, params?: Record<string, unknown>) => string;
  tSelected: () => string;
}): string {
  const blockedReasonCount = countBlockedStatusFilterValues(selectedValues);
  const nonBlockedReasonValues = selectedValues.filter(
    (value) => !blockedReasonFromStatusFilterValue(value),
  );
  const hasBlockedParent = nonBlockedReasonValues.includes(
    SignalStatus.BLOCKED,
  );
  const otherStatuses = nonBlockedReasonValues.filter(
    (value) => value !== SignalStatus.BLOCKED,
  );
  const hasBlockedSelection = hasBlockedParent || blockedReasonCount > 0;
  const onlyBlockedSelection =
    otherStatuses.length === 0 && hasBlockedSelection;

  if (onlyBlockedSelection && blockedReasonCount > 0) {
    const blockedLabel = t('status.BLOCKED');
    const reasonCountLabel = t('status.blockedReasonCount', {
      count: blockedReasonCount,
    });

    return `${blockedLabel} ${reasonCountLabel}`;
  }

  const statusSelectionCount = getSignalStatusSelectionCount(selectedValues);

  if (statusSelectionCount === 1) {
    if (hasBlockedSelection) {
      return t('status.BLOCKED');
    }

    return t(`status.${otherStatuses[0]}` as never);
  }

  return `(${statusSelectionCount}) ${tSelected()}`;
}

export function resolveBlockedSignalFilters({
  status,
  blockedReason,
}: {
  status?: SignalStatusType[];
  blockedReason?: BlockedReasonType[];
}): {
  status?: SignalStatusType[];
  blockedReason?: BlockedReasonType[];
} {
  const hasAllBlockedReasons =
    blockedReason &&
    blockedReason.length === BLOCKED_REASON_VALUES.length &&
    BLOCKED_REASON_VALUES.every((reason) => blockedReason.includes(reason));

  if (hasAllBlockedReasons) {
    const nextStatus = status ?? [];

    return {
      status: nextStatus.includes(SignalStatus.BLOCKED)
        ? nextStatus
        : [...nextStatus, SignalStatus.BLOCKED],
      blockedReason: undefined,
    };
  }

  if (blockedReason?.length) {
    const nextStatus = status?.filter(
      (statusValue) => statusValue !== SignalStatus.BLOCKED,
    );

    return {
      status: nextStatus?.length ? nextStatus : undefined,
      blockedReason,
    };
  }

  return { status, blockedReason };
}

export function splitSignalStatusFilterValues(
  values: readonly string[] | undefined,
): {
  status?: SignalStatusType[];
  confirmedVulnerabilityFindingState?: ConfirmedVulnerabilityFindingStateType[];
  blockedReason?: BlockedReasonType[];
} {
  const status: SignalStatusType[] = [];
  const confirmedVulnerabilityFindingState: ConfirmedVulnerabilityFindingStateType[] =
    [];
  const blockedReason: BlockedReasonType[] = [];

  values?.forEach((value) => {
    if ((SIGNAL_STATUS_FILTER_VALUES as readonly string[]).includes(value)) {
      status.push(value as SignalStatusType);

      return;
    }

    const blockedReasonValue = blockedReasonFromStatusFilterValue(value);

    if (blockedReasonValue) {
      blockedReason.push(blockedReasonValue);

      return;
    }

    if (
      Object.hasOwn(
        CONFIRMED_VULNERABILITY_FINDING_STATE_BY_STATUS_FILTER_VALUE,
        value,
      )
    ) {
      confirmedVulnerabilityFindingState.push(
        CONFIRMED_VULNERABILITY_FINDING_STATE_BY_STATUS_FILTER_VALUE[
          value as ConfirmedVulnerabilityFindingStateStatusFilterValue
        ],
      );
    }
  });

  return {
    status: status.length > 0 ? status : undefined,
    confirmedVulnerabilityFindingState:
      confirmedVulnerabilityFindingState.length > 0
        ? confirmedVulnerabilityFindingState
        : undefined,
    blockedReason: blockedReason.length > 0 ? blockedReason : undefined,
  };
}

export function getConfirmedVulnerabilityFindingStateStatusFilterValues(
  states: readonly ConfirmedVulnerabilityFindingStateType[] | undefined,
): ConfirmedVulnerabilityFindingStateStatusFilterValue[] {
  return (
    states?.map(
      (state) =>
        STATUS_FILTER_VALUE_BY_CONFIRMED_VULNERABILITY_FINDING_STATE[state],
    ) ?? []
  );
}
